import { useEffect, useRef, useState } from "react";
import { cashPaymentService } from "../../services/cash-payment.service";

interface CashPaymentModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface ClientResult {
  id: string;
  name: string;
  email: string;
  phone: string | null;
}

interface InvoiceResult {
  id: string;
  number: string;
  total: number;
  status: string;
  date_issued: number;
  date_due: number;
  paid_amount: number;
  project_name: string | null;
}

interface LocationResult {
  id: string;
  name: string;
  location_type: string;
}

export function CashPaymentModal({ onClose, onSuccess }: CashPaymentModalProps) {
  const [step, setStep] = useState<"client" | "payment">("client");

  // Búsqueda de cliente
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState<ClientResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientResult | null>(null);

  // Facturas
  const [invoices, setInvoices] = useState<InvoiceResult[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceResult | null>(null);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

  // Ubicaciones
  const [locations, setLocations] = useState<LocationResult[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");

  // Formulario de pago
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cargar ubicaciones al iniciar
  useEffect(() => {
    cashPaymentService.getLocations().then(setLocations).catch(console.error);
  }, []);

  // Búsqueda con debounce
  useEffect(() => {
    if (clientQuery.trim().length < 2) {
      setClientResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await cashPaymentService.searchClients(clientQuery);
        setClientResults(results);
      } catch (err) {
        console.error(err);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [clientQuery]);

  const handleSelectClient = async (client: ClientResult) => {
    setSelectedClient(client);
    setStep("payment");
    setIsLoadingInvoices(true);
    try {
      const invs = await cashPaymentService.getClientInvoices(client.id);
      setInvoices(invs);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const handleSelectInvoice = (inv: InvoiceResult) => {
    setSelectedInvoice(inv);
    const pending = Math.max(inv.total - inv.paid_amount, 0);
    setAmount(pending.toFixed(2));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setReceiptFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setReceiptPreview(ev.target?.result as string);
    reader.readAsDataURL(f);
  };

  const handleSubmit = async () => {
    if (!selectedClient) {
      setError("Selecciona un cliente");
      return;
    }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setError("Ingresa un monto válido");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // 1. Subir foto del recibo (si la hay)
      let receiptPhotoUrl: string | undefined;
      if (receiptFile) {
        receiptPhotoUrl = await cashPaymentService.uploadReceiptPhoto(
          receiptFile,
          selectedClient.id,
          selectedInvoice?.id
        );
      }

      // 2. Registrar el pago
      const result = await cashPaymentService.registerCashPayment({
        clientId: selectedClient.id,
        invoiceId: selectedInvoice?.id || null,
        projectId: null,
        amount: amt,
        concept: selectedInvoice
          ? `Pago en efectivo factura ${selectedInvoice.number}`
          : "Pago en efectivo en oficina",
        locationId: selectedLocationId || undefined,
        receiptPhotoUrl,
        notes: notes.trim() || undefined,
      });

      setSuccess(
        `Pago de $${amt.toFixed(2)} registrado. Recibo entregado al cliente.`
      );
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      setError(err?.message || "Error al registrar pago");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              💵 Registrar Pago en Efectivo
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {step === "client" ? "Paso 1 de 2: Selecciona el cliente" : "Paso 2 de 2: Detalles del pago"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {step === "client" && (
            <>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Buscar cliente
                </label>
                <input
                  type="text"
                  value={clientQuery}
                  onChange={(e) => setClientQuery(e.target.value)}
                  placeholder="Nombre, email o teléfono..."
                  autoFocus
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none"
                />
              </div>

              {isSearching && (
                <p className="text-sm text-slate-500 text-center py-4">Buscando...</p>
              )}

              {!isSearching && clientQuery.length >= 2 && clientResults.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">
                  Sin resultados
                </p>
              )}

              {clientResults.length > 0 && (
                <ul className="space-y-2">
                  {clientResults.map((c) => (
                    <li key={c.id}>
                      <button
                        onClick={() => handleSelectClient(c)}
                        className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-primary/40 hover:bg-slate-50 transition"
                      >
                        <p className="font-semibold text-slate-900">{c.name}</p>
                        <p className="text-xs text-slate-500">
                          {c.email}
                          {c.phone && ` • ${c.phone}`}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {step === "payment" && selectedClient && (
            <>
              {/* Cliente seleccionado */}
              <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Cliente</p>
                  <p className="font-semibold text-slate-900">{selectedClient.name}</p>
                  <p className="text-xs text-slate-500">{selectedClient.email}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedClient(null);
                    setStep("client");
                    setSelectedInvoice(null);
                    setAmount("");
                  }}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Cambiar
                </button>
              </div>

              {/* Facturas */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Factura a pagar (opcional)
                </label>
                {isLoadingInvoices ? (
                  <p className="text-sm text-slate-500 py-2">Cargando facturas...</p>
                ) : invoices.length === 0 ? (
                  <p className="text-sm text-slate-500 py-2">
                    El cliente no tiene facturas pendientes. Puedes registrar un pago "a cuenta".
                  </p>
                ) : (
                  <ul className="space-y-2 max-h-48 overflow-y-auto">
                    <li>
                      <button
                        onClick={() => {
                          setSelectedInvoice(null);
                          setAmount("");
                        }}
                        className={`w-full text-left p-3 rounded-lg border transition ${
                          !selectedInvoice
                            ? "border-primary bg-primary-light"
                            : "border-slate-200 hover:border-primary/40"
                        }`}
                      >
                        <p className="font-medium text-sm">Sin factura específica (pago a cuenta)</p>
                      </button>
                    </li>
                    {invoices.map((inv) => {
                      const pending = Math.max(inv.total - inv.paid_amount, 0);
                      const isSelected = selectedInvoice?.id === inv.id;
                      return (
                        <li key={inv.id}>
                          <button
                            onClick={() => handleSelectInvoice(inv)}
                            className={`w-full text-left p-3 rounded-lg border transition ${
                              isSelected
                                ? "border-primary bg-primary-light"
                                : "border-slate-200 hover:border-primary/40"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-sm text-slate-900">
                                  {inv.number}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {inv.project_name || "Sin proyecto"}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-slate-900">
                                  ${pending.toFixed(2)}
                                </p>
                                <p className="text-xs text-slate-400">
                                  de ${inv.total.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Monto */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Monto recibido <span className="text-primary">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-lg font-bold"
                />
              </div>

              {/* Ubicación */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Ubicación de recepción
                </label>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-sm"
                >
                  <option value="">Mi wallet personal (default)</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Si no especificas ubicación, el efectivo entra a tu wallet personal y luego lo mueves al cajón.
                </p>
              </div>

              {/* Foto del recibo */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Foto del recibo entregado (opcional)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  capture="environment"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:font-medium hover:file:bg-slate-200 cursor-pointer"
                />
                {receiptPreview && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-slate-200 max-w-[200px]">
                    <img src={receiptPreview} alt="Recibo" className="w-full" />
                  </div>
                )}
              </div>

              {/* Notas */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Notas (opcional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  maxLength={200}
                  placeholder="Observaciones..."
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-sm resize-none"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg p-3">
                  {success}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {step === "payment" && (
          <div className="flex gap-3 px-6 py-4 border-t border-slate-200">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 py-2.5 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !amount || parseFloat(amount) <= 0}
              className="flex-1 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50"
            >
              {isSubmitting ? "Registrando..." : "Registrar pago"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}